import React, { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import { getUserAuth } from "../../../recoil/selectors";
import { TrackAnalyticsEvent } from "../../../services/Analytics";
import { AnalyticsEventNames } from "../../../utils/analyticsEvents";
import { ResultsTrackerMode } from "../../../utils/hooks/useResultsTrackerConfig";
import { DeriveTrackerInput, deriveCurrentStep } from "./trackerSteps";
import { MinimalTracker } from "./MinimalTracker";
import { DetailedTracker } from "./DetailedTracker";

interface OrderStatusTrackerProps {
  input: DeriveTrackerInput;
  mode: ResultsTrackerMode;
  showEstimatedDate: boolean;
  /** Variant string for analytics (e.g. "minimal_show_date"). */
  variant: string;
  /** Test this tracker represents, for analytics (e.g. "aneuploidy" / "carrier"). */
  testName: string;
  /** Section heading displayed above the tracker (e.g. "Aneuploidy Screen"). */
  title: string;
}

/**
 * Renders the patient results "Order status" tracker in the style dictated by
 * the experiment variant, and fires a one-time impression event.
 */
export const OrderStatusTracker: React.FC<OrderStatusTrackerProps> = ({
  input,
  mode,
  showEstimatedDate,
  variant,
  testName,
  title,
}) => {
  const auth = useRecoilValue(getUserAuth);
  const currentStep = deriveCurrentStep(input);
  const hasTrackedImpression = useRef(false);

  useEffect(() => {
    if (hasTrackedImpression.current || !auth?.access_token) {
      return;
    }
    hasTrackedImpression.current = true;
    TrackAnalyticsEvent(
      AnalyticsEventNames.ResultsTrackerShown,
      auth.access_token,
      auth.user_id,
      {
        variant,
        mode,
        test: testName,
        step: currentStep,
      },
    );
    // Impression fires once per mount per test; reruns (like token refreshes) are guarded above.
  }, [auth?.access_token, auth?.user_id, currentStep, mode, variant, testName]);

  if (mode === "detailed") {
    return (
      <DetailedTracker
        input={input}
        showEstimatedDate={showEstimatedDate}
        title={title}
      />
    );
  }
  return (
    <MinimalTracker
      input={input}
      showEstimatedDate={showEstimatedDate}
      title={title}
    />
  );
};
